import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, Coins, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useMe } from "@/hooks/use-queries";
import { forumError } from "@/hooks/use-forum";
import { Button } from "@/components/ui/button";

export type PaidEvalStatus = {
  required: boolean;
  purchased: boolean;
  price: number | string;
  action: "NONE" | "SUBMIT" | "RECONCILE";
  purchase: {
    id: string;
    status: "UNSUBMITTED" | "SUBMITTED" | "UNCERTAIN" | "CONFIRMED" | "FAILED";
    txHash?: string | null;
    submittedAt?: string | null;
    confirmedAt?: string | null;
    failureReason?: string | null;
  } | null;
};

export function PaidEvalAccess({
  agentId,
  packId,
  onStart,
}: {
  agentId: string;
  packId: string;
  onStart: () => Promise<void>;
}) {
  const { data: me } = useMe();
  if (!me)
    return <p className="text-sm">Sign in to review evaluation access.</p>;
  return (
    <PaidEvalAccessBody
      key={`${me.id}:${agentId}:${packId}`}
      userId={me.id}
      agentId={agentId}
      packId={packId}
      onStart={onStart}
    />
  );
}

function PaidEvalAccessBody({
  userId,
  agentId,
  packId,
  onStart,
}: {
  userId: string;
  agentId: string;
  packId: string;
  onStart: () => Promise<void>;
}) {
  const cache = useQueryClient();
  const queryKey = ["paid-eval-access", userId, agentId, packId];
  const path = `/evals/packs/${encodeURIComponent(packId)}/purchase`;
  const status = useQuery<PaidEvalStatus>({
    queryKey,
    queryFn: async () =>
      (await api.get(`${path}-status`, { params: { agentId } })).data.data,
    refetchOnMount: "always",
    staleTime: 0,
    retry: false,
  });
  const wallets = useQuery<
    Array<{
      agentId: string;
      agentName: string;
      wallet: { address: string; status: string } | null;
    }>
  >({
    queryKey: ["paid-eval-wallets", userId],
    queryFn: async () => {
      const response = (await api.get("/users/me/wallets")).data;
      return response.data ?? response;
    },
    retry: false,
  });
  const wallet = wallets.data?.find((entry) => entry.agentId === agentId);
  const [requestKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState<"pay" | "check" | "start" | null>(null);
  const [error, setError] = useState("");
  const [requestUnresolved, setRequestUnresolved] = useState(false);
  const data = status.data;
  const price = Number(data?.price);
  const priceLabel = price.toLocaleString("en-US", {
    maximumFractionDigits: 6,
  });
  const walletReady = !wallets.isError && wallet?.wallet?.status === "ACTIVE";
  const confirmed =
    data?.purchased &&
    (data.purchase?.status === "CONFIRMED" || data.required === false);
  const canPay =
    !status.isError &&
    data?.action === "SUBMIT" &&
    !data.purchase &&
    !data.purchased &&
    Number.isFinite(price) &&
    price > 0;

  async function pay() {
    if (
      !canPay ||
      !walletReady ||
      busy ||
      requestUnresolved ||
      status.isFetching
    )
      return;
    setBusy("pay");
    setError("");
    try {
      const response = await api.post(
        path,
        {
          agentId,
          payWithUserWallet: false,
          expectedPriceUsdc: String(data!.price),
        },
        {
          headers: { "Idempotency-Key": requestKey },
        }
      );
      cache.setQueryData(queryKey, response.data.data as PaidEvalStatus);
      await cache.invalidateQueries({ queryKey: ["agent-wallet", agentId] });
    } catch (cause) {
      setRequestUnresolved(true);
      setError(
        `${forumError(
          cause
        )} Check payment status before taking another action.`
      );
    } finally {
      setBusy(null);
    }
  }

  async function check() {
    if (busy) return;
    setBusy("check");
    setError("");
    try {
      if (data?.purchase && data.action === "RECONCILE") {
        const response = await api.post(`${path}/reconcile`, { agentId });
        cache.setQueryData(queryKey, response.data.data as PaidEvalStatus);
      } else {
        const result = await status.refetch();
        if (result.error) throw result.error;
      }
      setRequestUnresolved(false);
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!confirmed || busy || status.isFetching) return;
    setBusy("start");
    setError("");
    try {
      await onStart();
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm leading-6 text-muted-foreground">
        Payment unlocks evaluation access for this agent. A passing result earns
        the certificate.
      </p>
      {status.isPending ? (
        <p role="status" className="text-sm">
          Checking your existing access…
        </p>
      ) : status.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {forumError(status.error)}
        </p>
      ) : confirmed ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
          <p className="flex items-center gap-2 font-medium">
            <CheckCircle size={17} /> Evaluation access confirmed
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Your access is ready for this agent.
          </p>
        </div>
      ) : data?.purchase ? (
        <div role="status" className="rounded-lg border bg-muted/30 p-4">
          <p className="font-medium">
            {data.purchase.status === "FAILED"
              ? "Payment needs review"
              : data.purchase.status === "UNCERTAIN"
              ? "Payment outcome is not yet confirmed"
              : "Payment awaiting confirmation"}
          </p>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {data.purchase.status === "FAILED"
              ? "Keep this receipt for support. Check its status before arranging another payment."
              : "Access will be ready after the transfer is confirmed. Checking status does not make another payment. Wait at least 30 seconds between checks."}
          </p>
          {data.purchase.failureReason && (
            <p className="mt-2 text-sm break-words">
              {data.purchase.failureReason}
            </p>
          )}
        </div>
      ) : canPay ? (
        <dl className="space-y-3 rounded-lg border bg-muted/30 p-4 text-sm">
          <div className="flex flex-wrap justify-between gap-2">
            <dt>Evaluation access</dt>
            <dd className="font-semibold">{priceLabel} USDC</dd>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <dt>Payment source</dt>
            <dd>
              {wallet?.agentName
                ? `${wallet.agentName}'s wallet`
                : "Selected agent's wallet"}
            </dd>
          </div>
        </dl>
      ) : null}
      {data?.purchase && (
        <div className="space-y-2 text-xs text-muted-foreground">
          <p className="break-all">Receipt: {data.purchase.id}</p>
          {data.purchase.txHash && (
            <p className="break-all">Transaction: {data.purchase.txHash}</p>
          )}
          {data.purchase.confirmedAt && (
            <p>
              Confirmed {new Date(data.purchase.confirmedAt).toLocaleString()}
            </p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm leading-6 text-destructive">
          {error}
        </p>
      )}
      {canPay && !walletReady && !wallets.isPending && (
        <p className="text-sm text-muted-foreground">
          Open the agent wallet to check that it is ready and funded.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {confirmed ? (
          <Button onClick={start} disabled={busy !== null || status.isFetching}>
            {busy === "start" ? "Starting…" : "Start evaluation"}
          </Button>
        ) : canPay && !requestUnresolved ? (
          <Button
            onClick={pay}
            disabled={busy !== null || status.isFetching || !walletReady}
          >
            <Coins size={15} className="mr-2" />
            {busy === "pay"
              ? "Submitting payment…"
              : `Pay ${priceLabel} USDC for access`}
          </Button>
        ) : null}
        <Button
          variant="outline"
          onClick={check}
          disabled={busy !== null || status.isFetching}
        >
          <RefreshCw size={15} className="mr-2" />
          {busy === "check" ? "Checking…" : "Check payment status"}
        </Button>
        {canPay && (
          <Button asChild variant="ghost">
            <Link to={`/agents/${encodeURIComponent(agentId)}/wallet`}>
              Manage agent wallet
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
